import React, { useState, useEffect, useCallback } from 'react';
import { Button, Row, Col, Form, Select, Card } from 'antd';
import TextArea from '../../../ui/TextArea';
import Collapse, { Panel } from '../../../ui/Collapse';
import { PlusOutlined } from '@ant-design/icons';
import './index.css';
import PageContainer from '../../../CSIComponents/PageContainer';
import {
  propsTypes,
  itemType,
  detailDataType,
  policiesType,
  recyclingType,
  optionsDataType,
} from './types';
import CSIActionsButtonsComponent from '../../../Globals/CsiActionButtonsComponent';
import { setData, post, getHelper } from './helpers';
import Loader from '../../../Loading';

const defaultRow = [
  {
    desc: '',
  },
];

const RecyclingProgram = (props: propsTypes) => {
  const FormItem = Form.Item;
  const Option = Select.Option;
  const options: string[] = ['Yes', 'No'];
  const [activeprogram, setActiveprogram] = useState<string>();
  const [ecologicaldishware, setEcologicaldishware] = useState<string>();
  const [id, setId] = useState<string | undefined | null>();
  const [langId, setLangId] = useState<string | undefined | null>();
  const [dinamycMargin, setDinamycMargin] = useState<number>(60);
  //must rerender Collapse after add as we need new active keys
  const [refreshCount, setRefreshCount] = useState(0);
  const [detailData, setDetailData] = useState<detailDataType>(defaultRow);
  const [optionsData, setOptionsData] = useState<optionsDataType>();
  const [isonEdit, setIsOnEdit] = useState<boolean>(false);
  const [description, setDescription] = useState<string[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const [isDisable, setIsDisabled] = useState<boolean>(false);

  //only need to change data which can controlling Panel rendering
  const deleteItem = (index: number) => {
    const _detailData = JSON.parse(JSON.stringify(detailData));
    _detailData.splice(index, 1);
    setDetailData(_detailData);
    // setting up margin bottom on empty emails
    if (_detailData.length < 1) {
      setDinamycMargin(90);
    }
  };

  //need add object and refresh active keys of <Collapse />, which means we need to rerender <Collapse />
  const addItem = useCallback(() => {
    const newDetails = Object.assign(detailData, [
      ...detailData,
      {
        desc: '',
      },
    ]);
    setDetailData(newDetails);
    setRefreshCount(refreshCount + 1);
    setDinamycMargin(60);
  }, [detailData, refreshCount]);

  const getRecycling = useCallback(async () => {
    /* istanbul ignore next */
    try {
      const response: recyclingType = await getHelper(
        '/property/environmentalimpact'
      );
      setId(response?.property?.environmentalimpact?.id);
      setLangId(response?.property?.environmentalimpact?.langid);
      // if eservice id used in order to identify if it is a post or put call
      if (response?.property?.environmentalimpact?.recyclingprogram) {
        const records =
          response?.property?.environmentalimpact?.recyclingprogram.description;
        // setting up the records data
        if (records) {
          if (records.length > 0) {
            setDetailData(records);
          } else {
            // setting up a default view
            const tempObj = Object.assign(
              detailData,
              response?.property?.environmentalimpact?.recyclingprogram
                .description
            );
            setDetailData(tempObj);
          }
        }
        setActiveprogram(
          response?.property?.environmentalimpact?.recyclingprogram
            .activeprogram
        );
        setEcologicaldishware(
          response?.property?.environmentalimpact?.recyclingprogram
            .ecologicaldishware
        );
        setOptionsData(
          // @ts-expect-error
          response?.property?.environmentalimpact?.recyclingprogram
        );
        return setReady(true);
      }
    } catch (error) {
      /* istanbul ignore next */
      return error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persitingData = async () => {
    try {
      const formingResponse = {
        activeprogram,
        ecologicaldishware,
        description: detailData,
      };
      setIsDisabled(true);
      setReady(false);
      await post(formingResponse, id, langId);
      setIsOnEdit(!isonEdit);
      props.setOperationType('view');
      setIsDisabled(false);
      await getFromServer();
      props.disableTabPanes(false);
      return setReady(true);
    } catch (error) {
      setReady(true);
      throw new Error('Error while saving data');
    }
  };

  const getFromServer = async () => {
    const response: recyclingType = await getHelper(
      '/property/environmentalimpact'
    );
    // if eservice id used in order to identify if it is a post or put call
    if (response?.property?.environmentalimpact?.recyclingprogram) {
      const records =
        response?.property?.environmentalimpact?.recyclingprogram.description;
      // setting up the records data
      if (records) {
        if (records.length > 0) {
          setDetailData(records);
        } else {
          // setting up a default view
          const tempObj = Object.assign(
            detailData,
            response?.property?.environmentalimpact?.recyclingprogram
              .description
          );
          setDetailData(tempObj);
        }
      } else {
        const cleanRow = [
          {
            desc: '',
          },
        ];
        setDetailData(cleanRow);
      }
      // @ts-expect-error
      setOptionsData(response?.property?.environmentalimpact?.recyclingprogram);
      setActiveprogram(
        response?.property?.environmentalimpact?.recyclingprogram.activeprogram
      );
      setEcologicaldishware(
        response?.property?.environmentalimpact?.recyclingprogram
          .ecologicaldishware
      );
    }
  };

  const onClickCancel = async () => {
    setReady(false);
    await getFromServer();
    setIsOnEdit(!isonEdit);
    props.setOperationType('view');
    props.disableTabPanes(false);
    setReady(true);
  };

  useEffect(() => {
    getRecycling();
  }, [getRecycling]);

  return (
    <PageContainer>
      <Card
        style={{ marginTop: -15, marginBottom: 50 }}
        title="Recycling Program"
        headStyle={{
          fontWeight: 600,
          fontSize: 14,
          color: '#333',
          paddingTop: '8px',
          paddingLeft: '4px',
        }}
        extra={
          <Button
            type="primary"
            className={!isonEdit ? 'db-edit-btn' : 'd-none'}
            onClick={() => {
              setIsOnEdit(true);
              props.setOperationType('edit');
              props.disableTabPanes(true);
            }}
          >
            Edit
          </Button>
        }
      >
        {ready && optionsData && (
          <Form
            layout="vertical"
            className={!isonEdit ? 'is-disabled-fields' : ''}
            initialValues={{
              activeprogram: optionsData?.activeprogram || '',
              ecologicaldishware: optionsData?.ecologicaldishware || '',
            }}
          >
            <Row gutter={24}>
              <Col span={6}>
                <FormItem
                  name="activeprogram"
                  label="Active Program"
                >
                  <Select allowClear showSearch
                    onChange={(e: string) => setActiveprogram(e)}
                  >
                    {options.map((opt: string, index: number) => (
                      <Option
                        value={opt}
                        key={`activeprogram-${index}`}
                      >
                        {opt}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem
                  name="ecologicaldishware"
                  label="Ecological Dishware"
                >
                  <Select allowClear showSearch
                    onChange={(e: string) => setEcologicaldishware(e)}
                  >
                    {options.map((opt: string, index: number) => (
                      <Option
                        value={opt}
                        key={`ecologicaldishware-${index}`}
                      >
                        {opt}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>
          </Form>
        )}
        <Collapse
          defaultActiveKey={detailData.map((_: object, i: number) => i)}
          key={refreshCount}
          style={{ marginBottom: dinamycMargin }}
          multiCollapseButton
        >
          <>
            {ready &&
              detailData.map((item: itemType, i: number) => (
                <React.Fragment key={i}>
                  {item ? (
                    <Panel
                      header={`Description ${i + 1}`}
                      key={i}
                      deleteButton={
                        i === 1 && {
                          action: () => deleteItem(i),
                          title: `Are you sure you want to delete Description ${
                            i + 1
                          }?`,
                        }
                      }
                    >
                      <Form
                        className={!isonEdit ? 'is-disabled-fields' : ''}
                        layout="vertical"
                        name="basic"
                        initialValues={{
                          description: item.desc,
                        }}
                      >
                        <Row gutter={24}>
                          {console.log(item)}
                          <Col span={18}>
                            <Form.Item
                              label="Description"
                              name="description"
                              rules={[
                                {
                                  max: 300,
                                  message:
                                    'You have reached the max character length of 300',
                                },
                              ]}
                            >
                              <TextArea
                                maxLength={300}
                                edit={isonEdit}
                                rows={1}
                                onChange={(e: {
                                  target: { value: string };
                                }) => {
                                  setData(
                                    'desc',
                                    i,
                                    e.target.value,
                                    description,
                                    detailData,
                                    setDescription
                                  );
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    </Panel>
                  ) : null}
                </React.Fragment>
              ))}
            {!ready && <Loader type="medium" />}
            {isonEdit && (
              <div>
                <Button
                  type="link"
                  onClick={(e) => {
                    e.preventDefault();
                    addItem();
                  }}
                  icon={<PlusOutlined />}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#fff',
                    float: 'right',
                  }}
                >
                  {`Add Description${detailData.length + 1}`}
                </Button>
              </div>
            )}
          </>
        </Collapse>
        {isonEdit && (
          <CSIActionsButtonsComponent
            setIsOnEdit={() => onClickCancel()}
            disableFlag={isDisable}
            update={() => persitingData()}
          />
        )}
      </Card>
    </PageContainer>
  );
};

export default RecyclingProgram;
